import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import type { FormikErrors, FormikHelpers } from "formik";
import { Formik, Form as FormikForm } from "formik";
import { useGetMenuItemDetailsQuery } from "@/graphql/detailsMenuItem/detailsMenuItem.hooks";
import { BASE_URL } from "@/constants";
import {
  buildAssetUrl,
  formatMinVolumeText,
  formatPriceText,
} from "@/utils/mapMenuItemToDishCardProps";
import { useTranslation } from "react-i18next";
import type {
  ICartDishItem,
  IDishDetailsFormValues,
  IDishDetailsModalViewModel,
  TSupplement,
} from "@/types";
import ResponsiveModal from "@/components/ui/responsiveModal";
import DishDetailsModalContent from "@/pages/menuItemsPage/components/dishDetailsModalContent";
import SpinnerCustom from "@/components/ui/spinner";
import RenderDishMarkers from "@/pages/menuItemsPage/components/renderDishMarkers";
import styles from "@/components/ui/responsiveModal/responsiveModal.module.scss";
import { buildInitialDishDetailsFormValues } from "@/pages/menuItemsPage/helpers";
import { findFirstInvalidRequiredGroupId } from "@/pages/menuItemsPage/helpers/supplementsHelpers";
import DishDetailsModalFooter from "@/pages/menuItemsPage/components/dishDetailsModalFooter";
import {
  getCartDish,
  sanitizeSupplementsValue,
  upsertCartDish,
} from "@/components/cart/services/cartService";
import { normalizeCartDishItemAgainstDetails } from "@/components/cart/helpers/normalizeCartDishItemAgainstDetails";
import { pickLocalizedText } from "@/utils/languageVersions/pickLocalizedText";
import { openToast } from "@/components/ui/toastComponent/helpers/toastUiEvents";
import { getMenuTypeFromUrl } from "@/components/cart/helpers/cartEvents";
import { useGetMenuIdQuery } from "@/graphql/menuId/menuId.hooks";

interface DishDetailsModalContainerProps {
  isOpen: boolean;
  dishId: number | null;
  categoryId: number | null;
  onClose: () => void;
}

const FORM_ID = "dish-details-form";

const DishDetailsModalContainer: React.FC<DishDetailsModalContainerProps> = ({
  isOpen,
  dishId,
  categoryId,
  onClose,
}) => {
  const { t } = useTranslation();
  const { locale, menuId, branchId, typeMenu } = useParams();
  const menuTypeEnum = getMenuTypeFromUrl(typeMenu);

  const { data: menuIdData } = useGetMenuIdQuery({
    skip: !branchId || !menuTypeEnum,
    variables: {
      branchId: Number(branchId),
      typeMenu: menuTypeEnum,
    },
  });

  const menuData = menuIdData?.menu?.id;
  const currentMenuId = menuId ? Number(menuId) : menuData;

  const { data: menuItemDetailsData, loading: menuItemDetailsLoading } =
    useGetMenuItemDetailsQuery({
      skip: !locale || !dishId || !branchId,
      variables: {
        menuId: menuId ? Number(menuId) : null,
        branchId: Number(branchId),
        locale,
        dishId: dishId,
        categoryId: categoryId,
        typeMenu: menuId ? null : menuTypeEnum,
      },
      context: {
        headers: {
          "X-LOCALE": locale,
        },
      },
    });

  const menuItem = menuItemDetailsData?.menu?.items?.[0] ?? null;
  const currency = menuItemDetailsData?.menu?.branch?.currencyCode ?? "";

  const viewModel: IDishDetailsModalViewModel | null = useMemo(() => {
    if (!menuItem?.id) return null;
    const categoryIconPath = menuItem?.categories?.[0]?.favicon?.path ?? null;
    const imageUrl = buildAssetUrl(BASE_URL, menuItem.favicon);
    const fallbackCategoryIconUrl = buildAssetUrl(BASE_URL, categoryIconPath);
    const title = pickLocalizedText(menuItem.name, menuItem.nameOrigin) ?? "";
    const description = pickLocalizedText(
      menuItem.description,
      menuItem.descriptionOrigin
    );
    const composition = pickLocalizedText(
      menuItem.composition,
      menuItem.compositionOrigin
    );
    const unit = pickLocalizedText(menuItem.unit?.name, null) ?? "";

    const portions = (menuItem.portions ?? [])
      .map((portion) => {
        const portionName = pickLocalizedText(portion.name, portion.nameOrigin);
        if (!portionName) return null;

        return {
          ...portion,
          name: portionName,
        };
      })
      .filter(
        (portion): portion is NonNullable<typeof portion> => portion != null
      );

    const supplements: TSupplement[] = (menuItem.supplements ?? [])
      .map((supplement) => {
        const options =
          supplement.options
            ?.map((option) => {
              const optionName = pickLocalizedText(
                option.name,
                option.nameOrigin
              );
              if (!optionName) return null;

              return {
                id: option.id,
                name: optionName,
                price: option.price ?? null,
              };
            })
            .filter(
              (option): option is NonNullable<typeof option> => option != null
            ) ?? [];

        const groupName = pickLocalizedText(
          supplement.groupName,
          supplement.groupNameOrigin
        );
        if (!groupName || options.length === 0) return null;

        return {
          id: supplement.id,
          groupName,
          numberOfOption: supplement.numberOfOption ?? null,
          isRequireOnOrder: supplement.isRequireOnOrder ?? false,
          maxCountOnOrder: supplement.maxCountOnOrder ?? null,
          price: supplement.price ?? null,
          options,
        };
      })
      .filter(
        (supplement): supplement is NonNullable<typeof supplement> =>
          supplement != null
      );

    return {
      title,
      description,
      composition,
      imageUrl,
      fallbackCategoryIconUrl,
      markers: menuItem.markers ?? [],
      allergens: menuItem.allergens ?? [],
      portions,
      cookingTime: menuItem.cookingTime ?? null,
      isLongCookingTime: menuItem.isLongCookingTime,
      basicPrice: formatPriceText(portions, currency, t),
      basicVolume: formatMinVolumeText(portions, unit, t),
      supplements,
      currency,
      unit,
    };
  }, [menuItem, currency, t]);

  const validate = (values: IDishDetailsFormValues) => {
    const errors: FormikErrors<IDishDetailsFormValues> = {};

    if (!values.portionId) {
      errors.portionId = "required";
    }

    if (!values.dishQuantity || values.dishQuantity < 1) {
      errors.dishQuantity = "min_1";
    }

    return errors;
  };

  const handleSubmit = (
    values: IDishDetailsFormValues,
    formikHelpers: FormikHelpers<IDishDetailsFormValues>
  ) => {
    try {
      const invalidGroupId = findFirstInvalidRequiredGroupId(
        viewModel?.supplements ?? [],
        values.supplements
      );

      if (invalidGroupId != null) {
        formikHelpers.setStatus({
          invalidRequiredGroupId: invalidGroupId,
        });
        return;
      }

      formikHelpers.setStatus({
        invalidRequiredGroupId: null,
      });

      if (!currentMenuId || !dishId || !values.portionId || !viewModel) return;

      const draft: ICartDishItem = {
        dishId,
        portionId: values.portionId,
        supplements: sanitizeSupplementsValue(values.supplements),
        quantity: values.dishQuantity,
        sourceCategoryId: categoryId ?? null,
      };

      const { normalized } = normalizeCartDishItemAgainstDetails(
        draft,
        viewModel
      );
      upsertCartDish(Number(currentMenuId), normalized);
      onClose();
      openToast("Saved");
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  const footer =
    menuItemDetailsLoading || !viewModel ? (
      <div className="d-flex justify-content-center py-2">
        <SpinnerCustom />
      </div>
    ) : (
      <DishDetailsModalFooter viewModel={viewModel} formId={FORM_ID} />
    );

  const markersForHeader = viewModel ? (
    <RenderDishMarkers
      markers={viewModel.markers}
      allergens={viewModel.allergens}
    />
  ) : null;

  const initState = useMemo(() => {
    const defaults = buildInitialDishDetailsFormValues(viewModel);

    const emptyState: {
      formInitialValues: IDishDetailsFormValues;
      normalizedCartItemToPersist: ICartDishItem | null;
    } = {
      formInitialValues: defaults,
      normalizedCartItemToPersist: null,
    };

    if (!isOpen || !currentMenuId || !dishId || !viewModel) return emptyState;

    const saved = getCartDish(Number(currentMenuId), dishId);
    if (!saved) return emptyState;

    const { normalized, changed } = normalizeCartDishItemAgainstDetails(
      saved,
      viewModel
    );

    const formInitialValues: IDishDetailsFormValues = {
      ...defaults,
      portionId: normalized.portionId,
      dishQuantity: normalized.quantity,
      supplements: normalized.supplements,
    };

    return {
      formInitialValues,
      normalizedCartItemToPersist: changed ? normalized : null,
    };
  }, [viewModel, isOpen, currentMenuId, dishId]);

  const normalizedToPersist = initState.normalizedCartItemToPersist;

  useEffect(() => {
    if (!isOpen || !currentMenuId || !normalizedToPersist) return;
    upsertCartDish(Number(currentMenuId), normalizedToPersist);
  }, [isOpen, currentMenuId, normalizedToPersist]);

  return (
    <Formik<IDishDetailsFormValues>
      initialValues={initState.formInitialValues}
      enableReinitialize
      validate={validate}
      onSubmit={handleSubmit}
    >
      {() => {
        return (
          <ResponsiveModal
            isOpen={isOpen}
            onClose={onClose}
            footer={footer}
            headerClassName={styles.headerShadow}
            footerClassName={styles.footerShadow}
            bodyClassName={"pt-3 px-5"}
            headerLeft={
              <div className="d-flex flex-wrap align-items-center gap-2 min-w-0">
                <h5 className="m-0 flex-grow-0 flex-shrink-1">
                  {viewModel?.title ?? ""}
                </h5>
                <div className="d-flex flex-wrap gap-1">{markersForHeader}</div>
              </div>
            }
          >
            <FormikForm id={FORM_ID} className="m-0">
              {viewModel ? (
                <DishDetailsModalContent
                  isLoading={menuItemDetailsLoading}
                  viewModel={viewModel}
                />
              ) : (
                <div className="d-flex justify-content-center py-5">
                  <SpinnerCustom />
                </div>
              )}
            </FormikForm>
          </ResponsiveModal>
        );
      }}
    </Formik>
  );
};

export default DishDetailsModalContainer;
